import { Theme } from '@logto/schemas';
import { noop } from '@silverhand/essentials';
import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { SignInExperienceResponse, Platform } from '@/types';

import type { PageContextType } from './PageContext';
import MainContext from './PageContext';

type Props = {
  readonly children: React.ReactNode;
  readonly preset?: Partial<
    Pick<
      PageContextType,
      | 'theme'
      | 'toast'
      | 'loading'
      | 'platform'
      | 'termsAgreement'
      | 'experienceSettings'
      | 'isPreview'
    >
  >;
};

const getInitialPlatform = (): Platform =>
  document.body.classList.contains('mobile') ? 'mobile' : 'web';

const PageContextProvider = ({ children, preset }: Props) => {
  const [searchParameters] = useSearchParams();

  const [loading, setLoading] = useState(preset?.loading ?? false);
  const [toast, setToast] = useState(preset?.toast ?? '');
  // Authentication keeps one dark appearance, including embedded and console previews.
  const theme = Theme.Dark;
  const setTheme = noop;

  const [platform, setPlatform] = useState<Platform>(preset?.platform ?? getInitialPlatform);
  const [termsAgreement, setTermsAgreement] = useState(preset?.termsAgreement ?? false);
  const [experienceSettings, setExperienceSettings] = useState<
    SignInExperienceResponse | undefined
  >(preset?.experienceSettings ?? undefined);

  const isPreview = searchParameters.get('preview') === 'true';

  const pageContext = useMemo<PageContextType>(
    () => ({
      theme,
      toast,
      loading,
      platform,
      termsAgreement,
      experienceSettings,
      isPreview,
      setTheme,
      setLoading,
      setToast,
      setPlatform,
      setTermsAgreement,
      setExperienceSettings,
    }),
    [experienceSettings, isPreview, loading, platform, termsAgreement, theme, toast, setTheme]
  );

  return <MainContext.Provider value={pageContext}>{children}</MainContext.Provider>;
};

export default PageContextProvider;
